import { useApp } from "@/lib/AppContext";
import { personColour, personInitials } from "@/lib/personAvatar";

/**
 * UI-8 on a phone — the gallery, and only the gallery.
 *
 * The rows do not open a dossier, and that is the handoff's line rather than an omission:
 * "Full dossiers, voiceprints and speaker matching stay on the desktop." A dossier is where
 * relations are edited, aliases are set and a voice is confirmed, and every one of those is
 * a decision that wants the evidence beside it.
 *
 * So this screen answers one question — who does the system know, and is that name right —
 * and a row is a row rather than a link to a page that would have to refuse. It does not
 * say so in a footer: somebody who taps a row and finds it inert has learned it, and a
 * standing line under every gallery is a caveat about the desktop on the screen that is
 * least able to do anything about it. `desktopOnly()` still names it at `/people/<id>`,
 * which is where the question is actually being asked.
 */
export function PhonePeople() {
  const { people, peopleLoading, peopleProblem } = useApp();

  return (
    <div className="flex flex-col gap-2.5 p-4 [animation:omFadeUp_220ms_cubic-bezier(0.4,0,0.2,1)_both]">
      <p className="text-[13px] text-[var(--text-muted)] [text-wrap:pretty]">
        Everyone the system has heard, matched by voice across recordings.
      </p>

      {peopleProblem && (
        <p role="alert" className="text-[13px] text-[var(--warning)]">
          {peopleProblem}
        </p>
      )}

      {peopleLoading ? (
        <p className="text-[13px] text-[var(--text-muted)]">Loading…</p>
      ) : people.length === 0 ? (
        <p className="py-8 text-center text-[14px] leading-[1.5] text-[var(--text-secondary)] [text-wrap:pretty]">
          Nobody yet. People arrive when you confirm who spoke in a recording, which is done from the
          desktop app.
        </p>
      ) : (
        <>
          {people.map((person) => (
            <div
              key={person.id}
              className="flex items-center gap-3 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3.5 py-3"
            >
              <span
                aria-hidden
                className="flex size-10 shrink-0 items-center justify-center rounded-full text-[14px] font-bold text-[var(--text-inverse)]"
                style={{ background: personColour(person.id) }}
              >
                {personInitials(person.name)}
              </span>

              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[15px] font-bold text-[var(--text-primary)]">{person.name}</span>
                  {person.isMe && (
                    <span className="shrink-0 rounded-[var(--radius-full)] bg-[var(--vl-life)] px-2 py-0.5 text-[10px] font-semibold text-[var(--text-inverse)]">
                      You
                    </span>
                  )}
                </div>
                <span className="truncate text-[12px] text-[var(--text-muted)]">
                  {person.role ??
                    `${person.meetingCount} ${person.meetingCount === 1 ? "meeting" : "meetings"}`}
                </span>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
